import type { DisciplineShow } from '@ts-discipline/interfaces'

import { useDisciplineStore } from '@stores/discipline'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface DisciplineShowApiOptions {
  disciplineId: string | number
  /** Leave the layout loader alone: the caller shows its own (the discipline page loader). */
  silent?: boolean
  onStart?: () => void
  onSuccess?: (data: DisciplineShow) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useDisciplineShowApi = async (options: DisciplineShowApiOptions): Promise<DisciplineShow | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.disciplines.api.show', { discipline_id: options.disciplineId })

  const disciplineStore = useDisciplineStore()
  const commonStore = useCommonStore()

  const setLoading = (value: boolean): void => {
    if (!options.silent) commonStore.setLoading(value)
  }

  options.onStart?.()
  setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const discipline: DisciplineShow = data.data

    disciplineStore.setDiscipline(discipline)
    setLoading(false)

    options.onSuccess?.(discipline)

    return discipline
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    setLoading(false)
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}